import { useCallback, useEffect, useState } from 'react'
import { ChevronDown, Loader2, MapPin, Palette, Phone, Save } from 'lucide-react'

import { BookLogo } from '@/components/booking/PublicBookShell'
import { Button } from '@/components/ui/button'
import { useBranch } from '@/contexts/BranchContext'
import {
  fetchOnlineBookingSettings,
  updateOnlineBookingSettings,
} from '@/lib/api/onlineBookingSettings'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { cn } from '@/lib/utils'

const inputClass = cn(
  'h-9 w-full rounded-lg border border-border/80 bg-background px-2.5 text-sm',
  'focus:outline-none focus:ring-2 focus:ring-ring',
)

function socialUrl(links, key) {
  return links.find((link) => link.key === key)?.url ?? ''
}

function buildSocialLinks(form) {
  const links = []

  if (form.whatsapp?.trim()) {
    const digits = form.whatsapp.replace(/\D+/g, '')
    if (digits) {
      links.push({
        key: 'whatsapp',
        label: 'WhatsApp',
        url: `https://wa.me/${digits}`,
      })
    }
  }

  if (form.instagram_url?.trim()) {
    links.push({
      key: 'instagram',
      label: 'Instagram',
      url: form.instagram_url.trim(),
    })
  }

  return links
}

function settingsToForm(settings) {
  const contact = settings?.contact ?? {}
  const links = settings?.social_links ?? []
  const titleLines = settings?.hero_title_lines ?? ['']

  return {
    logo_url: settings?.logo_url ?? '',
    hero_image_url: settings?.hero_image_url ?? '',
    hero_title_line_1: titleLines[0] ?? '',
    hero_title_line_2: titleLines[1] ?? '',
    hero_subline: settings?.hero_subline ?? 'BOOK & RELAX',
    phone: contact.phone ?? '',
    whatsapp: contact.whatsapp ?? '',
    address: contact.address ?? '',
    map_url: contact.map_url ?? '',
    instagram_url: socialUrl(links, 'instagram'),
  }
}

function Field({ label, children, className = '' }) {
  return (
    <label className={cn('block space-y-1', className)}>
      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      {children}
    </label>
  )
}

export function OnlineBookingBrandingPanel({ className = '' }) {
  const { activeBranch, activeBranchId } = useBranch()
  const [form, setForm] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const load = useCallback(async () => {
    if (!activeBranchId) {
      setForm(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')

    try {
      const settings = await fetchOnlineBookingSettings()
      if (!settings?.enabled && !settings?.is_enabled) {
        setForm(null)
        return
      }
      setForm(settingsToForm(settings))
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to load storefront settings.'))
      setForm(null)
    } finally {
      setLoading(false)
    }
  }, [activeBranchId])

  useEffect(() => {
    load()
  }, [load])

  function updateField(field, value) {
    setForm((current) => (current ? { ...current, [field]: value } : current))
    setSuccess('')
  }

  async function handleSave(event) {
    event.preventDefault()
    if (!form) {
      return
    }

    setSaving(true)
    setError('')
    setSuccess('')

    const heroTitleLines = [form.hero_title_line_1, form.hero_title_line_2]
      .map((line) => line.trim())
      .filter(Boolean)

    try {
      await updateOnlineBookingSettings({
        logo_url: form.logo_url.trim() || null,
        hero_image_url: form.hero_image_url.trim() || null,
        hero_title_lines: heroTitleLines.length > 0 ? heroTitleLines : [activeBranch?.name ?? 'Salon'],
        hero_subline: form.hero_subline.trim() || 'BOOK & RELAX',
        phone: form.phone.trim() || null,
        whatsapp: form.whatsapp.trim() || null,
        address: form.address.trim() || null,
        map_url: form.map_url.trim() || null,
        social_links: buildSocialLinks(form),
      })
      setSuccess('Guest page settings saved.')
      setExpanded(false)
      await load()
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to save storefront settings.'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className={cn('card-surface flex items-center gap-2 p-3 text-sm text-muted-foreground', className)}>
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading guest page settings…
      </div>
    )
  }

  if (!form) {
    return (
      <div className={cn('card-surface p-3 text-sm text-muted-foreground', className)}>
        Online booking is not enabled for this branch yet.
      </div>
    )
  }

  const previewTitle = [form.hero_title_line_1, form.hero_title_line_2].filter(Boolean).join(' ') || activeBranch?.name
  const contactSummary = [form.phone, form.whatsapp].filter(Boolean).join(' · ')

  return (
    <form onSubmit={handleSave} className={cn('card-surface overflow-hidden', className)}>
      <button
        type="button"
        onClick={() => setExpanded((open) => !open)}
        className="flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-muted/30"
        aria-expanded={expanded}
      >
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#1F1611]">
          <BookLogo url={form.logo_url || null} name={previewTitle} className="text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Palette className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
            <h2 className="text-sm font-semibold text-foreground">Guest page branding</h2>
          </div>
          <p className="truncate text-xs font-medium text-foreground">{previewTitle}</p>
          <p className="truncate text-[11px] text-muted-foreground">
            {[form.hero_subline, contactSummary].filter(Boolean).join(' · ') || 'Logo, name, contact for QR landing page'}
          </p>
        </div>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', expanded && 'rotate-180')}
          aria-hidden
        />
      </button>

      {expanded ? (
        <div className="space-y-3 border-t border-border/60 px-3 pb-3 pt-3">
          <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Logo URL">
              <input
                className={inputClass}
                value={form.logo_url}
                onChange={(event) => updateField('logo_url', event.target.value)}
                placeholder="https://…/logo.png"
              />
            </Field>
            <Field label="Hero image URL">
              <input
                className={inputClass}
                value={form.hero_image_url}
                onChange={(event) => updateField('hero_image_url', event.target.value)}
                placeholder="https://…/hero.jpg"
              />
            </Field>
            <Field label="Tagline">
              <input
                className={inputClass}
                value={form.hero_subline}
                onChange={(event) => updateField('hero_subline', event.target.value)}
                placeholder="BOOK & RELAX"
              />
            </Field>
            <Field label="Name line 1">
              <input
                className={inputClass}
                value={form.hero_title_line_1}
                onChange={(event) => updateField('hero_title_line_1', event.target.value)}
                placeholder={activeBranch?.name ?? 'Salon name'}
              />
            </Field>
            <Field label="Name line 2">
              <input
                className={inputClass}
                value={form.hero_title_line_2}
                onChange={(event) => updateField('hero_title_line_2', event.target.value)}
                placeholder="Optional"
              />
            </Field>
          </div>

          <div className="rounded-lg border border-border/50 bg-muted/15 px-2.5 py-2">
            <p className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              <Phone className="h-3 w-3" aria-hidden />
              Contact (guest menu)
            </p>
            <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Phone">
                <input
                  className={inputClass}
                  value={form.phone}
                  onChange={(event) => updateField('phone', event.target.value)}
                  placeholder="+971 50 …"
                />
              </Field>
              <Field label="WhatsApp">
                <input
                  className={inputClass}
                  value={form.whatsapp}
                  onChange={(event) => updateField('whatsapp', event.target.value)}
                  placeholder="+971 50 …"
                />
              </Field>
              <Field label="Instagram">
                <input
                  className={inputClass}
                  value={form.instagram_url}
                  onChange={(event) => updateField('instagram_url', event.target.value)}
                  placeholder="https://instagram.com/…"
                />
              </Field>
              <Field label="Address" className="sm:col-span-2">
                <input
                  className={inputClass}
                  value={form.address}
                  onChange={(event) => updateField('address', event.target.value)}
                  placeholder="Street, area, city"
                />
              </Field>
              <Field label="Map link">
                <div className="relative">
                  <MapPin className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <input
                    className={cn(inputClass, 'pl-8')}
                    value={form.map_url}
                    onChange={(event) => updateField('map_url', event.target.value)}
                    placeholder="Maps URL"
                  />
                </div>
              </Field>
            </div>
          </div>

          {error ? <p className="text-xs text-red-600">{error}</p> : null}
          {success ? <p className="text-xs text-emerald-700">{success}</p> : null}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setExpanded(false)}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={saving} className="shadow-luxury-sm">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
              Save
            </Button>
          </div>
        </div>
      ) : null}

      {!expanded && error ? <p className="border-t border-border/60 px-3 py-2 text-xs text-red-600">{error}</p> : null}
      {!expanded && success ? (
        <p className="border-t border-border/60 px-3 py-2 text-xs text-emerald-700">{success}</p>
      ) : null}
    </form>
  )
}
